import type { DashboardCell, DashboardConfig, DashboardLayout } from '../types/gotypes'

/**
 * Normalise a dashboard config as it comes off the wire (#1245).
 *
 * goapi serialises an empty `Rows []DashboardRow` as JSON `null`, so a
 * dashboard created through the API with no top-level layout (scripts, the
 * agent, exec-report tooling — the UI always writes at least one row) arrives
 * as `layout: {rows: null}`, and every `for (const row of layout.rows)` in the
 * editor throws "layout.rows is not iterable". Fix it ONCE where the dashboard
 * is loaded rather than guarding every walker.
 *
 * Returns the SAME object when nothing needs fixing, so react-query's
 * structural sharing and every downstream memo keep their references.
 */
export function normalizeDashboardConfig(config: DashboardConfig): DashboardConfig {
  let changed = false

  const fixCells = (cells: DashboardCell[] | null | undefined): DashboardCell[] => {
    const list = Array.isArray(cells) ? cells : []
    if (!Array.isArray(cells)) changed = true
    let out = list
    list.forEach((cell, i) => {
      if (cell?.layout_data) {
        const fixed = fixLayout(cell.layout_data)
        if (fixed !== cell.layout_data) {
          if (out === list) out = [...list]
          out[i] = { ...cell, layout_data: fixed }
        }
      }
    })
    return out
  }

  const fixLayout = (layout: DashboardLayout | null | undefined): DashboardLayout => {
    if (!layout || !Array.isArray(layout.rows)) {
      changed = true
      return { ...(layout || {}), rows: [] }
    }
    let rows = layout.rows
    layout.rows.forEach((row, i) => {
      const cells = fixCells(row?.cells)
      if (cells !== row?.cells) {
        if (rows === layout.rows) rows = [...layout.rows]
        rows[i] = { ...row, cells }
      }
    })
    return rows === layout.rows ? layout : { ...layout, rows }
  }

  const layout = fixLayout(config.layout)
  let pages = config.pages
  if (Array.isArray(config.pages)) {
    config.pages.forEach((page, i) => {
      const fixed = fixLayout(page.layout)
      if (fixed !== page.layout) {
        if (pages === config.pages) pages = [...(config.pages as NonNullable<typeof config.pages>)]
        ;(pages as NonNullable<typeof pages>)[i] = { ...page, layout: fixed }
      }
    })
  }

  if (!changed) return config
  return { ...config, layout, pages }
}
